import { ApiService } from "./AbstractApiService";
import qs from "qs";
import { IPageModel } from "@/interfaces/iPageModel";
import {
  IForms,
  ISite,
  ISiteDevices,
  ISiteLocationDevices,
  ISiteUser,
  ISiteDeviceModel,
  ISiteActivity,
} from "@/interfaces/iSite";
import API from "./handlers/base.service";

export class SiteApi extends ApiService {
  public static getInstance() {
    if (!this.instance) {
      this.instance = new this();
    }
    return this.instance;
  }

  private static instance: SiteApi | null;
  constructor() {
    super("site");
  }
  public async getSiteList(
    params?: Record<string, string | number>
  ): Promise<IPageModel<ISite>> {
    return this.get("sites", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public async getScheduleSite(
    params?: Record<string, string | number>
  ): Promise<IPageModel<ISite>> {
    return this.get("sites/schedule/sites", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public async addSite(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<ISite>> {
    if (edit) {
      return this.put("sites/" + payload?.webId, payload).then(
        (response) => response.data
      );
    } else {
      return this.post("sites", payload).then((response) => response.data);
    }
  }
  public bulkDeleteEntity(entity: string, params: string[]) {
    return this.service
      .delete(`site/${entity}`, { data: params })
      .then((response) => response.data);
  }

  public async getSiteUserList(
    params?: Record<string, string | number>
  ): Promise<IPageModel<ISiteUser>> {
    return this.get(`userSites`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getSiteUserAndRoleList(
    params?: Record<string, string[] | number[]>
  ): Promise<IPageModel<ISiteUser>> {
    return this.get(`userSites/userAndRole`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async addSiteUser(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<ISiteUser>> {
    if (edit) {
      return this.put("userSites/" + payload?.webId, payload).then(
        (response) => response.data
      );
    } else {
      return this.post("userSites", payload).then((response) => response.data);
    }
  }
  public async addMultiSiteUser(
    payload?: { siteId: number; userAccountId: number; roleId: number }[],
    edit?: boolean
  ): Promise<IPageModel<ISiteUser>> {
    if (edit) {
      return this.put(
        "userSites/assignUsers/" + payload?.[0]?.siteId,
        payload
      ).then((response) => response.data);
    } else {
      return this.post("userSites/assignUsers", payload).then(
        (response) => response.data
      );
    }
  }

  public async getLookupData(payload?: Record<string, string | number>) {
    //
  }

  public async getSiteById(id: number): Promise<ISite> {
    return this.get(`sites/${id}`).then((response) => response.data);
  }

  public async getSiteFormsList(
    id: number,
    params?: Record<string, string | number>
  ): Promise<IPageModel<IForms>> {
    return this.get(`siteForms/${id}/forms`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async submitCloneSite(data): Promise<any> {
    return this.post("sites/global", data).then((response) => response.data);
  }

  public async deleteSiteForms(
    id: number,
    payload: any
  ): Promise<IPageModel<IForms>> {
    return this.service
      .delete(`site/siteForms/${id}/forms`, { data: payload })
      .then((response) => response.data);
  }

  public async deleteSiteUsers(
    id: number,
    payload: any
  ): Promise<IPageModel<ISite>> {
    return this.service
      .delete(`site/siteUsers/${id}/forms`, { data: payload })
      .then((response) => response.data);
  }

  public async getFormSitesList(
    id: number,
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<IForms>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get(`siteForms/${id}/sites`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async addSiteForm(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<IForms>> {
    if (edit) {
      return this.put(`siteForms/` + payload?.webId, payload).then(
        (response) => response.data
      );
    } else {
      return this.post(`siteForms`, payload).then((response) => response.data);
    }
  }

  public async getSiteLocationDevicesList(
    params: Record<string, string | number>
  ): Promise<IPageModel<ISiteLocationDevices>> {
    return this.get(`siteDevices`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async addSiteLocationDevicesList(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<ISiteLocationDevices>> {
    if (edit) {
      return this.put(`siteDevices/` + payload?.webId, payload).then(
        (response) => response.data
      );
    } else {
      return this.post(`siteDevices`, payload).then(
        (response) => response.data
      );
    }
  }

  public async getSiteDevicesList(
    params: Record<string, string | number>
  ): Promise<IPageModel<ISiteDevices>> {
    return this.get(`devices`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async addFormSites(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<IForms>> {
    if (edit) {
      return this.put(`siteForms/` + payload?.webId, payload).then(
        (response) => response.data
      );
    } else {
      return this.post(`siteForms`, payload).then((response) => response.data);
    }
  }

  public async addSiteDevicesList(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<ISiteDevices>> {
    if (edit) {
      return this.put(`devices/` + payload?.webId, payload).then(
        (response) => response.data
      );
    } else {
      return this.post(`devices`, payload).then((response) => response.data);
    }
  }
  public async getSite(id: number): Promise<ISite> {
    return this.get(`sites/${id}`).then((response) => response.data);
  }

  public async getSiteLocation(id: number): Promise<ISite> {
    return this.get(`locations/${id}`).then((response) => response.data);
  }

  public async getDevice(id: number): Promise<ISite> {
    return this.get(`devices/${id}`).then((response) => response.data);
  }

  public async getCountries(id?: number): Promise<any> {
    return this.get(`filter/COUNTRIES`).then((response) => response.data);
  }
  public async getStates(id?: number): Promise<any> {
    return this.get(`filter/STATES?userId=${id}`).then(
      (response) => response.data
    );
  }
  public async getCities(id?: number): Promise<any> {
    return this.get(`filter/CITIES?userId=${id}`).then(
      (response) => response.data
    );
  }
  public async getSiteDeviceList(
    id: number,
    params?: Record<string, string | number>
  ): Promise<IPageModel<ISiteDeviceModel>> {
    return this.service
      .get(`site/siteDevices/${id}/devices`, {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
      })
      .then((response) => response.data);
  }

  public async deleteSiteDevice(
    id: number,
    payload: any
  ): Promise<IPageModel<ISiteDeviceModel>> {
    return this.service
      .delete(`site/siteDevices/${id}/devices`, { data: payload })
      .then((response) => response.data);
  }

  public async addSiteDevice(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<ISiteDeviceModel>> {
    if (edit) {
      return this.service
        .put(`site/siteDevices` + payload?.webId, payload)
        .then((response) => response.data);
    } else {
      return this.service
        .post(`site/siteDevices`, payload)
        .then((response) => response.data);
    }
  }

  public async getDeviceSiteList(
    id: number,
    params?: Record<string, string | number>
  ): Promise<IPageModel<ISiteDeviceModel>> {
    return this.service
      .get(`site/siteDevices/${id}/sites`, {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
      })
      .then((response) => response.data);
  }

  public async deleteDeviceSite(
    id: number,
    payload: any
  ): Promise<IPageModel<ISite>> {
    return this.service
      .delete(`site/siteDevices/${id}/site`, { data: payload })
      .then((response) => response.data);
  }

  public async addDeviceSite(
    payload?: Record<string, string | number>,
    edit?: boolean
  ): Promise<IPageModel<ISite>> {
    if (edit) {
      return this.service
        .put(`site/siteDevices` + payload?.webId, payload)
        .then((response) => response.data);
    } else {
      return this.service
        .post(`site/siteDevices`, payload)
        .then((response) => response.data);
    }
  }

  public async addUserSiteRole(payload) {
    return this.service
      .put(`site/userSites/assignRole`, payload)
      .then((response) => response.data);
  }

  public transformDateRangeParams(
    params: Record<string, string | number>
  ): Record<string, string | number> {
    if (
      params &&
      params["startDate"] &&
      `${params["startDate"]}`.split(",").length > 1
    ) {
      const dates = `${params["startDate"]}`.split(",");
      params["startDate"] = dates[0];
      params["endDate"] = dates[1];
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ...rest } = params;
    return rest;
  }
}
export async function getMethod<T extends string>(
  url: string
): Promise<Array<Record<any, any>>> {
  return API.get(url).then((res) => {
    return res.data;
  });
}
